<template>
    <div class="app-content" v-if="items.length">
        <!-- Controls -->
        <grid-controls
            :current-page="currentPage"
            :per-page="perPage"
            :total-results="totalResults"
            @onResultsChange="onResultsChange"
            @onSearch="onSearch"
        ></grid-controls>

        <!-- Data Table -->
        <b-table
            :current-page="currentPage"
            :fields="fields"
            :filter="searchText"
            :items="filteredItems"
            :per-page="perPage"
            bordered
            class="mb-0"
            head-variant="dark"
            hover
            responsive
            sort-by="Participant"
            sticky-header
            striped
            v-show="totalResults > 0"
            @filtered="resetPaging"
            @row-clicked="loadRecord"
        >
            <template v-slot:cell(DataSourceType)="data">
                <span class="data-source" :title="data.value"
                    >{{ formatDataSource(data.value) }}
                </span>
            </template>

            <template v-slot:cell()="data">
                <fa-icon v-if="data.value === 'x'" icon="check"></fa-icon>
                <span v-else>{{ data.value }}</span>
            </template>
        </b-table>

        <!-- Footer -->
        <grid-footer
            @pageChange="onPageChange"
            :filtered-items="filteredItems"
            :per-page="perPage"
            :total-results="totalResults"
        ></grid-footer>

        <!-- No results -->
        <hec-alert
            class="m-3"
            icon="exclamation-triangle"
            variant="warning"
            v-if="totalResults === 0"
        >
            There are no Data Contributors matching the current filters
        </hec-alert>

        <!-- Modal -->
        <participant-modal :participant="currentRecord"></participant-modal>
    </div>
</template>

<script>
import GridControls from "./GridControls";
import GridFooter from "./GridFooter";
import ParticipantModal from "@/components/participant/ParticipantModal";

import { getData } from "@/shared/dataService.js";
import { FIELDS } from "@/shared/constants.js";
import { clone, filter, get, isEmpty, sortBy, uniq } from "lodash-es";

export default {
    name: "ParticipantGrid",
    components: {
        GridControls,
        GridFooter,
        ParticipantModal
    },
    props: {
        activeFilters: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            currentPage: 1,
            currentRecord: null,
            filteredItems: [],
            items: [],
            perPage: 20,
            searchText: null,
            totalResults: 0,
            fields: [
                {
                    key: "Participant",
                    sortable: true,
                    stickyColumn: true
                },
                {
                    key: "ZipCode",
                    sortable: true
                },
                {
                    key: "DataSourceType",
                    sortable: true
                },
                {
                    key: "ADT",
                    sortable: true
                },
                {
                    key: "LAB",
                    sortable: true
                },
                {
                    key: "RAD",
                    sortable: true
                },
                {
                    key: "TRN",
                    sortable: true
                },
                {
                    key: "Imaging",
                    sortable: true
                },
                {
                    key: "ReferenceLabs",
                    tdClass: "text-center",
                    thClass: "text-center",
                    sortable: true
                },
                {
                    key: "ReceivingCCDs",
                    sortable: true
                },
                {
                    key: "EKGs",
                    sortable: true,
                    serviceFilter: true
                },
                {
                    key: "VisitSummary",
                    tdClass: "text-center",
                    thClass: "text-center",
                    sortable: true,
                    serviceFilter: true
                },
                {
                    key: "Allergies",
                    tdClass: "text-center",
                    thClass: "text-center",
                    serviceFilter: true,
                    sortable: true
                },
                {
                    key: "CareManagement",
                    tdClass: "text-center",
                    thClass: "text-center",
                    serviceFilter: true,
                    sortable: true
                },
                {
                    key: "CareTeamMembers",
                    tdClass: "text-center",
                    thClass: "text-center",
                    serviceFilter: true,
                    sortable: true
                },
                {
                    key: "ConditionsProblems",
                    tdClass: "text-center",
                    thClass: "text-center",
                    serviceFilter: true,
                    sortable: true
                },
                {
                    key: "Immunization",
                    tdClass: "text-center",
                    thClass: "text-center",
                    serviceFilter: true,
                    sortable: true
                },
                {
                    key: "Medications",
                    tdClass: "text-center",
                    thClass: "text-center",
                    serviceFilter: true,
                    sortable: true
                },
                {
                    key: "Orders",
                    tdClass: "text-center",
                    thClass: "text-center",
                    serviceFilter: true,
                    sortable: true
                },
                {
                    key: "OrderResults",
                    tdClass: "text-center",
                    thClass: "text-center",
                    serviceFilter: true,
                    sortable: true
                },
                {
                    key: "PatientDemographics",
                    tdClass: "text-center",
                    thClass: "text-center",
                    serviceFilter: true,
                    sortable: true
                },
                {
                    key: "Procedures",
                    tdClass: "text-center",
                    thClass: "text-center",
                    serviceFilter: true,
                    sortable: true
                },
                {
                    key: "SmokingStatus",
                    tdClass: "text-center",
                    thClass: "text-center",
                    serviceFilter: true,
                    sortable: true
                },
                {
                    key: "VisitInformation",
                    tdClass: "text-center",
                    thClass: "text-center",
                    serviceFilter: true,
                    sortable: true
                },
                {
                    key: "Vitals",
                    tdClass: "text-center",
                    thClass: "text-center",
                    serviceFilter: true,
                    sortable: true
                }
            ]
        };
    },
    async mounted() {
        await this.applyFieldLabels();
        this.init();
    },
    watch: {
        activeFilters: {
            deep: true,
            handler(newValue) {
                this.applyFilters(newValue);
            }
        }
    },
    methods: {
        /**
         * Apply field labels from constant
         */
        applyFieldLabels() {
            this.fields.forEach(value => {
                const label = get(FIELDS[value.key], "short");
                if (label) value.label = label;

                const title = get(FIELDS[value.key], "long");
                if (title) value.headerTitle = title;
            });
        },

        /**
         * Create filters array for services
         */
        buildServiceFilterList() {
            let filterSet = [];
            this.fields.forEach(item => {
                if (item.serviceFilter) filterSet.push(item.key);
            });
            return sortBy(filterSet);
        },

        /**
         * Create filters from all unique values of a specific column.
         * @param {String} column
         */
        buildStandardFilterList(column) {
            let filterSet = [];
            this.items.forEach(row => {
                if (!isEmpty(row[column])) {
                    // If there are multiple values in the field, add each
                    // to the filter set
                    if (row[column].indexOf(",") > -1) {
                        const arr = row[column].split(",");
                        arr.forEach(value => {
                            const trimmedValue = value.trimStart().trimEnd();
                            filterSet.push(trimmedValue);
                        });
                    } else filterSet.push(row[column]);
                }
            });
            return sortBy(uniq(filterSet));
        },

        /**
         * Create object with an array for each set of filters.
         * Emit filterSet object so that filters get passed to sidebar.
         */
        async buildFilters() {
            let filterSet = {};

            filterSet.Services = await this.buildServiceFilterList();
            filterSet.DataSources = await this.buildStandardFilterList(
                "DataSourceType"
            );
            filterSet.ZipCodes = await this.buildStandardFilterList("ZipCode");

            this.$emit("filtersCreated", filterSet);
        },

        /**
         * Only show the first Data Source Type if there are multiple
         * @param {String} value - comma separated list
         */
        formatDataSource(value) {
            if (!value) return null;
            else return value.split(",")[0];
        },

        /**
         * Get data from JSON file
         */
        async init() {
            const response = await getData();

            if (response) {
                this.items = response;
                this.filteredItems = clone(this.items);
                this.totalResults = this.items.length;

                this.buildFilters();
            }

            this.$root.$emit("loadingEnd");
        },

        /**
         * Load current record on row click
         */
        loadRecord(record) {
            this.currentRecord = clone(record);
            this.$bvModal.show("participant-modal");
        },

        /**
         * Update number of rows per page when menu is changed
         */
        onResultsChange(perPage) {
            this.perPage = perPage;
        },

        /**
         * Filter data (via <b-table> :filter) when search text changes
         */
        onSearch(searchText) {
            this.searchText = searchText;
        },

        /**
         * Apply filters emitted from AppSidebar (via AppBody)
         */
        async applyFilters(filters) {
            let filteredData = clone(this.items);

            // Call filter sets sequentially so that each is applied against
            // the reduced data set
            // Filter Sets are ANDed (i.e. each set decreases the results)
            filteredData = await this.filterByManyFields(
                filteredData,
                filters.Services
            );
            filteredData = await this.filterByField(
                filteredData,
                filters.DataSources,
                "DataSourceType"
            );
            filteredData = await this.filterByField(
                filteredData,
                filters.ZipCodes,
                "ZipCode"
            );

            // Use $nextTick to ensure DOM update because this.filteredItems
            // is not reactive
            this.$nextTick(function() {
                this.filteredItems = filteredData;
                this.resetPaging(this.filteredItems);
            });
        },

        /**
         * Filter data by single field (Data Source or Zip)
         * Filters are ORed (i.e. each filter increases the results)
         * @param {Array} data - data set to filter
         * @param {Array} filter - list of filter values
         * @param {String} field - the field to filter against
         */
        filterByField(data, filters, field) {
            if (isEmpty(filters)) return data;

            return filter(data, row => {
                return filters.some(item => {
                    return !isEmpty(row[field])
                        ? row[field].includes(item)
                        : false;
                });
            });
        },

        /**
         * Filter data by one or more fields (Services)
         * Filters are ORed (i.e. each filter increases the results)
         */
        filterByManyFields(data, filters) {
            if (isEmpty(filters)) return data;

            return filter(data, function(row) {
                // Check if some (one or more) of the filtered columns have a value
                return filters.some(function(field) {
                    return !isEmpty(row[field]);
                });
            });
        },

        /**
         * Set current page
         */
        onPageChange(page) {
            this.currentPage = page;
        },

        /**
         * Make sure paging gets updated when data is filtered
         */
        resetPaging(filteredItems) {
            this.totalResults = filteredItems.length;
            this.currentPage = 1;
        }
    }
};
</script>

<style lang="scss">
@media screen {
    .app-content {
        display: flex;
        flex-direction: column;
        flex: 1;
        background: $gray-100;
        overflow-y: auto;
        z-index: 1;

        .b-table-sticky-header {
            // Make table visible area
            // 130px = .app-header + .controls + .pagination
            max-height: calc(100vh - 141px);

            // Adjust for increased sizes
            @include media-breakpoint-up(sm) {
                max-height: calc(100vh - 153px);
            }

            @include media-breakpoint-up(md) {
                max-height: calc(100vh - 180px);
            }

            // No need to use calc in flex layout
            @include media-breakpoint-up(lg) {
                flex: 1;
                max-height: none;
            }
        }

        @include media-breakpoint-up(lg) {
            .b-table-sticky-column {
                min-width: 180px;
            }
        }

        .footer {
            padding: $spacer-2 $spacer;

            .pagination {
                margin-bottom: 0;
            }
        }
    }
}
</style>
